import { PropsWithChildren, useState } from "react";
import { Column } from "../../utils/models";
import { BYTE_SIZE } from "../../utils/constants";

import * as XLSX from 'xlsx';

import AccordionItem from "../custom-reports/custom-visual-inspection/accordion-item";
import Button from "../themed/button";
import Conditional from "./conditional";
import ExcelExport from "./excelExport";
import FileImport from "../themed/file-import";
import PDFTablePage from "../themed/pdf/pdfTablePage";

import toastService from "../../services/toastService";

interface Props<T> extends PropsWithChildren{
    template: T[];
    generateTemplateColumns: (data: T[]) => Column[];
    onImportData: (data: T[]) => void;
}

const ExcelImport = <T extends unknown>(props: Props<T>) => {
   const [files, setFiles] = useState<File[]>([]);
   const [processedData, setProcessedData] = useState<T[]>([]);

    const handleFileRemove = (fileToRemove: File) => {
        const newFiles = files.filter((file) => file.name !== fileToRemove.name);
        setFiles(newFiles);
    };

    const handleFileDrop = async (acceptedFiles: File[]) => {
        setFiles(acceptedFiles);

        if (acceptedFiles && acceptedFiles.length > 0) {
            const reader = new FileReader();
    
            reader.onload = (event) => {
                const workbook = XLSX.read(event.target!.result, { type: 'buffer' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    
                const data = XLSX.utils.sheet_to_json(worksheet) as T[];
                setProcessedData(data);
            };

            reader.readAsArrayBuffer(acceptedFiles[0]);
        } else {
            toastService.error('Please upload a valid EXCEL file!');
        }
    };

    return (
        <AccordionItem title="Import Data From Excel" isActive>
            <div className="d-flex justify-content-end">
                <ExcelExport data={props.template} fileName="excel-template" label="Download Excel Template"  />
            </div>

            <FileImport
                files={files}
                onRemoveFile={handleFileRemove}
                onDrop={handleFileDrop}
                maxFiles={1} 
                maxSize={BYTE_SIZE * 2}
                accept={{ 'application/*': ['.xlsx', '.numbers'] }}
            />

            <Conditional isVisible={processedData.length > 0}>
                <div className="p-1 bg-light rounded bordered">
                    <PDFTablePage data={processedData} columns={props.generateTemplateColumns(processedData)} />
                </div>

                <div className="d-flex justify-content-end">
                    <Button 
                        label="Save & Import" 
                        icon="cloud-download-fill" 
                        onClick={() => {
                            props.onImportData(processedData);

                            setFiles([]);
                            setProcessedData([]);
                        }}
                    />
                </div>
            </Conditional>
        </AccordionItem>
    )
};
 
export default ExcelImport;